import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowRight,
  Check,
  CircleAlert,
  CircleDashed,
  ClipboardCopy,
  Loader,
  Lock,
} from "lucide-react";
import { useAuth } from "../../app/AuthContext";
import { api, ApiError } from "../../shared/api/client";
import type {
  LegState,
  ReleaseDetail,
  ReleaseJourney,
  ReleaseLeg,
} from "../../shared/types/api";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  InlineAlert,
  LoadingState,
  ProgressBar,
  StatusBadge,
} from "../../shared/ui";
import type { Tone } from "../../shared/ui/StatusBadge";
import { READINESS_LABELS, READINESS_TONE, formatReleaseMoment } from "./releaseVocabulary";

const STATE_LABELS: Record<LegState, string> = {
  LOCKED: "Locked",
  OPEN: "Not started",
  IN_PROGRESS: "In progress",
  DEPLOYING: "Deploying now",
  FAILED: "Last attempt failed",
  DEPLOYED: "Deployed",
};

const STATE_TONE: Record<LegState, Tone> = {
  LOCKED: "gray",
  OPEN: "gray",
  IN_PROGRESS: "blue",
  DEPLOYING: "amber",
  FAILED: "red",
  DEPLOYED: "green",
};

function StateIcon({ state }: { state: LegState }) {
  const className = "h-5 w-5";
  switch (state) {
    case "LOCKED":
      return <Lock className={`${className} text-ink-400`} aria-hidden />;
    case "DEPLOYED":
      return <Check className={`${className} text-green-600`} aria-hidden />;
    case "FAILED":
      return <CircleAlert className={`${className} text-red-600`} aria-hidden />;
    case "DEPLOYING":
      return <Loader className={`${className} animate-spin text-amber-600`} aria-hidden />;
    default:
      return <CircleDashed className={`${className} text-ink-400`} aria-hidden />;
  }
}

/**
 * The release as a journey, one card per environment (§62, §63).
 *
 * <p>This is the first thing somebody sees when they open a release, and it exists because the
 * question they arrive with is "where is this up to" — not "list every prerequisite anybody has
 * ever written for any environment". Development and production are different work on different
 * nights, and a merged list makes the second one unreadable: you cannot tell what you still have to
 * do from what somebody already did last week somewhere else.
 *
 * <p>A locked leg says why in words rather than greying out silently. "Production comes after
 * Development" is a rule somebody configured and can change; a disabled card with no explanation
 * reads as a bug and gets worked around.
 */
export function ReleaseJourneyPanel({
  release,
  onOpenLeg,
}: {
  release: ReleaseDetail;
  onOpenLeg: (environmentId: string) => void;
}) {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [copyError, setCopyError] = useState<string | null>(null);

  const journey = useQuery({
    queryKey: ["release-journey", release.id],
    queryFn: () => api.get<ReleaseJourney>(`/api/v1/releases/${release.id}/journey`),
  });

  const copyPlan = useMutation({
    mutationFn: (input: { fromEnvironmentId: string; toEnvironmentId: string }) =>
      api.post<ReleaseJourney>(`/api/v1/releases/${release.id}/journey/copy-plan`, input),
    onSuccess: () => {
      setCopyError(null);
      void queryClient.invalidateQueries({ queryKey: ["release-journey", release.id] });
      void queryClient.invalidateQueries({ queryKey: ["release-prerequisites", release.id] });
      void queryClient.invalidateQueries({ queryKey: ["release-execution", release.id] });
    },
    onError: (caught) =>
      setCopyError(caught instanceof ApiError ? caught.message : "Could not copy that plan."),
  });

  if (journey.isPending) {
    return (
      <Card>
        <CardBody>
          <LoadingState label="Working out the journey…" />
        </CardBody>
      </Card>
    );
  }

  const legs = journey.data?.legs ?? [];

  return (
    <Card>
      <CardHeader
        title="Where this release is going"
        subtitle={
          release.targetEnvironment
            ? `Its destination is ${release.targetEnvironment.name}. These are the environments on the way, in the order set on the Environments page.`
            : "No destination is set, so every environment is shown. Set one on Edit and this becomes the path to it."
        }
      />
      <CardBody className="space-y-3">
        {copyError && <InlineAlert tone="error">{copyError}</InlineAlert>}

        {legs.length === 0 ? (
          <EmptyState
            title="Nowhere to go"
            message="No environment is being offered. Add one under Administration → Environments."
          />
        ) : (
          legs.map((leg, index) => (
            <LegCard
              key={leg.environment.id}
              leg={leg}
              previous={legs[index - 1] ?? null}
              opensNext={
                legs[index + 1]?.state === "LOCKED"
                  ? legs[index + 1]?.environment.name ?? null
                  : null
              }
              release={release}
              canEdit={can("RELEASE_EDIT")}
              copying={copyPlan.isPending}
              onOpen={() => onOpenLeg(leg.environment.id)}
              onCopyFrom={(fromEnvironmentId) =>
                copyPlan.mutate({ fromEnvironmentId, toEnvironmentId: leg.environment.id })
              }
            />
          ))
        )}
      </CardBody>
    </Card>
  );
}

/**
 * What is actually true of this leg, in one sentence.
 *
 * <p>Ordered by what somebody needs to know rather than by what is easiest to compute. The first
 * version fell through to "0 checks must pass to deploy here" for a leg with no gate policy —
 * true, and useless: it said nothing about the fact that nothing had been deployed there, which
 * was the whole reason the next leg was still locked.
 */
function describe(leg: ReleaseLeg, release: ReleaseDetail, opensNext: string | null): string {
  if (leg.state === "LOCKED") {
    return leg.lockedReason ?? "Not open yet.";
  }
  if (leg.state === "DEPLOYED") {
    const when = formatReleaseMoment(leg.lastDeployedAt, release.timeZone);
    return leg.deploymentAttempts > 1
      ? `Deployed ${when} · ${leg.deploymentAttempts} attempts`
      : `Deployed ${when}`;
  }
  if (leg.state === "FAILED") {
    return `The last attempt failed. Recorded as attempt ${leg.deploymentAttempts}; try again when it is fixed.`;
  }
  if (leg.blockingReasons.length > 0) {
    return leg.blockingReasons[0] ?? "";
  }
  if (leg.deploymentAttempts === 0) {
    /* The common confusion: prerequisites ticked and the runbook run, so the leg looks finished,
       while nothing records that the release actually arrived. Ticking a runbook is not evidence
       of a deployment, and the next leg waits on the deployment. */
    return opensNext
      ? `Nothing has been deployed here yet — that is what opens ${opensNext}.`
      : "Nothing has been deployed here yet. Start a deployment to record that it arrived.";
  }
  if (leg.gatesRequired > 0) {
    return `${leg.gatesRequired} ${leg.gatesRequired === 1 ? "check" : "checks"} must pass to deploy here.`;
  }
  return "Nothing stands in the way of deploying here.";
}

function LegCard({
  leg,
  previous,
  opensNext,
  release,
  canEdit,
  copying,
  onOpen,
  onCopyFrom,
}: {
  leg: ReleaseLeg;
  previous: ReleaseLeg | null;
  /** The next leg waiting on this one, when one is — named so the reason is concrete. */
  opensNext: string | null;
  release: ReleaseDetail;
  canEdit: boolean;
  copying: boolean;
  onOpen: () => void;
  onCopyFrom: (fromEnvironmentId: string) => void;
}) {
  const locked = leg.state === "LOCKED";

  /* Offered only where it helps: this leg has no plan of its own and the one before it does.
     Copying into a leg that already has steps would duplicate them, and copying from an empty
     one would do nothing while looking like it worked. */
  const canCopy =
    canEdit
    && previous != null
    && leg.scopedPrerequisites + leg.scopedSteps === 0
    && previous.scopedPrerequisites + previous.scopedSteps > 0;

  return (
    <div
      className={`rounded-lg border p-3.5 ${
        locked ? "border-ink-200 bg-ink-50/60" : "border-ink-200 bg-white"
      }`}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          <span className="mt-0.5 shrink-0">
            <StateIcon state={leg.state} />
          </span>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <p className="font-medium text-ink-900">{leg.environment.name}</p>
              {leg.environment.production && <StatusBadge tone="red">Production</StatusBadge>}
              <StatusBadge tone={STATE_TONE[leg.state]}>{STATE_LABELS[leg.state]}</StatusBadge>
            </div>

            <p className="mt-1 text-xs text-ink-600">{describe(leg, release, opensNext)}</p>
          </div>
        </div>

        <div className="flex shrink-0 flex-wrap items-center gap-2">
          {!locked && (
            <StatusBadge tone={READINESS_TONE[leg.readiness]}>
              {READINESS_LABELS[leg.readiness]}
            </StatusBadge>
          )}
          {canCopy && previous && (
            <Button
              variant="ghost"
              size="sm"
              icon={<ClipboardCopy className="h-4 w-4" />}
              loading={copying}
              title={`Copy ${previous.environment.name}'s prerequisites and runbook here, unticked`}
              onClick={() => onCopyFrom(previous.environment.id)}
            >
              Copy from {previous.environment.name}
            </Button>
          )}
          <Button
            variant={locked ? "ghost" : "secondary"}
            size="sm"
            disabled={locked}
            icon={<ArrowRight className="h-4 w-4" />}
            onClick={onOpen}
          >
            {locked ? "Locked" : "Open"}
          </Button>
        </div>
      </div>

      {/* The two things that are actually per-environment work. Shown only where there is
          something to show — empty bars on a leg nobody has planned yet are noise. */}
      {!locked && (leg.prerequisitesTotal > 0 || leg.stepsTotal > 0) && (
        <div className="mt-3 grid gap-2 sm:grid-cols-2">
          {leg.prerequisitesTotal > 0 && (
            <ProgressBar
              value={leg.prerequisitesDone}
              total={leg.prerequisitesTotal}
              tone={leg.prerequisitesDone === leg.prerequisitesTotal ? "green" : "amber"}
              label="Pre-requisites"
            />
          )}
          {leg.stepsTotal > 0 && (
            <ProgressBar
              value={leg.stepsDone}
              total={leg.stepsTotal}
              tone={leg.stepsDone === leg.stepsTotal ? "green" : "amber"}
              label="Runbook steps"
            />
          )}
        </div>
      )}

      {canCopy && (
        <p className="mt-2 text-xs text-ink-500">
          Nothing has been written for {leg.environment.name} yet. Copying brings the wording
          across and nothing else — no ticks, no evidence, no times.
        </p>
      )}
    </div>
  );
}
